import { motion } from "framer-motion";
import { colorPalettes, getPaletteForSection } from "../utils/colorPalettes";
import type { ColorPalette } from "../types/narrative";

interface AnimatedBackgroundProps {
  colorIndex?: number;
  sectionId?: string; // Section ID for palette lookup
  palette?: ColorPalette; // Optional inline palette (takes precedence)
}

export function AnimatedBackground({
  colorIndex = 0,
  sectionId,
  palette: inlinePalette,
}: AnimatedBackgroundProps) {
  // Priority: 1) Inline palette, 2) Section-based lookup (with hash fallback), 3) Index-based
  const palette =
    inlinePalette ??
    (sectionId
      ? getPaletteForSection(sectionId)
      : colorPalettes[colorIndex % colorPalettes.length]);

  const deg = palette.degrees ?? 180;
  const gradientStops = palette.via
    ? `${palette.from} 0%, ${palette.via} 50%, ${palette.to} 100%`
    : `${palette.from} 0%, ${palette.to} 100%`;

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base color layer - animates on swipe */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: `linear-gradient(${deg}deg, ${gradientStops})`,
        }}
        transition={{
          duration: 0.8,
          ease: "easeInOut",
        }}
      />

      {/* Pulsing overlay that expands/contracts the middle color */}
      <motion.div
        key={colorIndex}
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{
          opacity: [0, 0.4, 0],
          scaleY: [0.8, 1.2, 0.8],
        }}
        style={{
          background: palette.via
            ? `linear-gradient(${deg}deg, transparent 20%, ${palette.via} 50%, transparent 80%)`
            : undefined,
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Animated overlay gradients for movement effect */}
      <motion.div
        className="absolute inset-0"
        animate={{
          opacity: [0.3, 0.5, 0.3],
          scale: [1, 1.1, 1],
          background: `linear-gradient(to bottom right, ${palette.from}60, transparent, transparent)`,
        }}
        transition={{
          opacity: { duration: 6, repeat: Infinity, ease: "easeInOut" },
          scale: { duration: 6, repeat: Infinity, ease: "easeInOut" },
          background: { duration: 0.8, ease: "easeInOut" },
        }}
      />

      <motion.div
        className="absolute inset-0"
        animate={{
          opacity: [0.2, 0.4, 0.2],
          scale: [1.1, 1, 1.1],
          background: `linear-gradient(to top left, ${palette.from}40, transparent, transparent)`,
        }}
        transition={{
          opacity: {
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          },
          scale: { duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 },
          background: { duration: 0.8, ease: "easeInOut" },
        }}
      />
    </div>
  );
}
